import React from "react";
import { Box, Checkbox, FormControlLabel, Stack, TextField, Typography } from "@mui/material";
import ComboBox from "../../../../component/common/ComboBox";
import EmployeeAutocomplete from "../../../../component/common/EmployeeAutocomplete";
import VendorAutocomplete from "../../../finance/components/vendor/VendorAutocomplete";
import GridField from "./GridField";
import { LEAVE_KIND_LABEL, COMPENSATION_LABEL } from "./codeLabels";

/**
 * 필드 하나를 타입에 맞게 그린다.
 *
 * <h3>왜 타입으로 흡수했나</h3>
 * 예전 모달은 결재 유형별로 손으로 짠 렌더러 570줄을 갖고 있었다. 거기엔 거래처 자동완성,
 * 프로젝트 채널 선택, 대체휴무 근거 결재 선택처럼 <b>단순 입력칸이 아닌 것</b>들이 섞여 있었다.
 * 그것들을 지우고 순수 동적 렌더러로 가면 화면이 지금보다 불편해진다 — 개편의 목적과 반대다.
 *
 * 그래서 그 UI 들을 <b>필드 타입</b>으로 끌어올렸다. 양식 정의가 {@code "type": "VENDOR"} 라고만
 * 적으면 거래처 자동완성이 그대로 나온다. 관리자가 새 양식을 만들어도 같은 편의 기능을 쓴다.
 */
export default function FieldRenderer({ field, value, onChange, context }) {
    const { type, label, required, options, help, placeholder } = field;
    const common = {
        size: "small",
        fullWidth: true,
        value: value ?? "",
        onChange: (e) => onChange(e.target.value),
        placeholder,
        InputProps: { sx: { fontSize: 13 } },
    };

    const wrap = (node) => (
        <Box sx={{ mb: 1.75 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#475569", mb: 0.5 }}>
                {label}
                {required && <Box component="span" sx={{ color: "#dc2626", ml: 0.4 }}>*</Box>}
            </Typography>
            {node}
            {help && (
                <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.4, lineHeight: 1.5 }}>
                    {help}
                </Typography>
            )}
        </Box>
    );

    switch (type) {
        // ── 숨김·파생 ────────────────────────────────────────────────────────
        // HIDDEN 은 값이 고정된 칸(예: 대체휴무 사용의 휴가종류). 화면에 안 그린다.
        case "HIDDEN":
            return null;

        // DERIVED 는 다른 칸에서 계산되는 값. 읽기 전용으로 보여주기만 한다 —
        // 숨기면 "며칠 차감되는지" 를 상신자가 확인할 수 없다.
        case "DERIVED":
            return wrap(
                <TextField {...common} value={value ?? "자동 계산"} disabled
                           InputProps={{ sx: { fontSize: 13, bgcolor: "#f8fafc" } }} />
            );

        // ── 기본 입력 ────────────────────────────────────────────────────────
        case "TEXTAREA":
            return wrap(<TextField {...common} multiline minRows={3} />);

        case "DATE":
            return wrap(<TextField {...common} type="date" InputLabelProps={{ shrink: true }} />);

        case "TIME":
            return wrap(<TextField {...common} type="time" InputLabelProps={{ shrink: true }} />);

        case "NUMBER":
            return wrap(<TextField {...common} type="number" />);

        case "EMAIL":
            return wrap(<TextField {...common} type="email" />);

        case "MONEY":
            return wrap(
                <TextField
                    {...common}
                    value={value === undefined || value === null || value === "" ? "" : Number(value).toLocaleString()}
                    onChange={(e) => {
                        const digits = e.target.value.replace(/[^\d]/g, "");
                        onChange(digits === "" ? "" : Number(digits));
                    }}
                    InputProps={{ sx: { fontSize: 13 }, endAdornment: <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>원</Typography> }}
                />
            );

        // ── 선택 ─────────────────────────────────────────────────────────────
        case "SELECT":
            return wrap(
                <ComboBox
                    clearable={false}
                    placeholder={`${label} 선택`}
                    value={value ?? ""}
                    onChange={onChange}
                    options={(options || []).map((o) => ({ value: o, label: displayLabel(field, o) }))}
                />
            );

        // 라벨과 선택지를 한 줄에 둔다. 선택지가 두세 개뿐이라 위아래로 나누면 세로만 길어진다.
        // 좁은 화면에서는 flexWrap 으로 자연스럽게 접힌다.
        //
        // 같은 항목을 다시 누르면 해제된다 — 라디오는 원래 해제가 안 되지만, 실수로 고른 뒤
        // 되돌릴 방법이 없으면 «선택 안 함» 을 표현하지 못한다. 필수 항목이면 비운 채로는
        // 상신이 막히므로(서버가 필수 검증) 해제를 열어 둬도 안전하다.
        case "RADIO":
            return (
                <Box sx={{ mb: 1.75 }}>
                    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" rowGap={0.5}>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>
                            {label}
                            {required && <Box component="span" sx={{ color: "#dc2626", ml: 0.4 }}>*</Box>}
                        </Typography>
                        {(options || []).map((o) => (
                            <FormControlLabel
                                key={o} sx={{ m: 0 }}
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={value === o}
                                        onChange={() => onChange(value === o ? null : o)}
                                    />
                                }
                                label={<Typography sx={{ fontSize: 13 }}>{displayLabel(field, o)}</Typography>}
                            />
                        ))}
                    </Stack>
                    {help && (
                        <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.4, lineHeight: 1.5 }}>
                            {help}
                        </Typography>
                    )}
                </Box>
            );

        case "CHECKBOX":
            return (
                <Box sx={{ mb: 1.75 }}>
                    <FormControlLabel
                        sx={{ m: 0 }}
                        control={<Checkbox size="small" checked={!!value} onChange={(e) => onChange(e.target.checked)} />}
                        label={<Typography sx={{ fontSize: 13 }}>{label}</Typography>}
                    />
                    {help && <Typography sx={{ fontSize: 11, color: "#94a3b8", ml: 4 }}>{help}</Typography>}
                </Box>
            );

        // 다중 선택 — 휴가 복구가 유일하게 이걸 쓴다(여러 종류를 한 번에 복구).
        case "CHECKBOX_GROUP": {
            const arr = Array.isArray(value) ? value : [];
            return wrap(
                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                    {(options || []).map((o) => (
                        <FormControlLabel
                            key={o} sx={{ m: 0, mr: 1 }}
                            control={
                                <Checkbox
                                    size="small"
                                    checked={arr.includes(o)}
                                    onChange={() => onChange(arr.includes(o) ? arr.filter((x) => x !== o) : [...arr, o])}
                                />
                            }
                            label={<Typography sx={{ fontSize: 13 }}>{displayLabel(field, o)}</Typography>}
                        />
                    ))}
                </Stack>
            );
        }

        // ── 참조 (기존 특수 UI 를 타입으로 흡수) ──────────────────────────────
        // 기안자 본인·본인 부서 — 고르는 게 아니라 박히는 값이다. 선택기로 두면 남을 골라 놓고
        // 상신할 수 있어 «작성인» 이 실제 기안자와 달라진다.
        case "SUBMITTER":
            return wrap(<Fixed text={context?.myInfo?.name || "—"} />);

        case "SUBMITTER_DEPT":
            return wrap(<Fixed text={context?.myInfo?.deptName || "소속 없음"} />);

        case "EMPLOYEE":
            return wrap(
                <EmployeeAutocomplete
                    value={value ?? null}
                    onChange={(emp) => onChange(emp && typeof emp !== "string" ? { id: emp.id, name: emp.name } : emp)}
                    placeholder={`${label} 선택`}
                    variant="outlined"
                    disableUnderline={false}
                />
            );

        case "EMPLOYEE_MULTI": {
            const arr = Array.isArray(value) ? value : [];
            return wrap(
                <Box>
                    <EmployeeAutocomplete
                        value={null}
                        onChange={(emp) => {
                            if (!emp || typeof emp === "string") return;
                            if (arr.some((x) => x.id === emp.id)) return;
                            onChange([...arr, { id: emp.id, name: emp.name }]);
                        }}
                        excludeIds={arr.map((x) => x.id)}
                        placeholder={`${label} 추가`}
                        variant="outlined"
                        disableUnderline={false}
                    />
                    {arr.length > 0 && (
                        <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.75 }}>
                            {arr.map((p) => (
                                <Box key={p.id}
                                     onClick={() => onChange(arr.filter((x) => x.id !== p.id))}
                                     sx={{
                                         px: 1, py: 0.25, borderRadius: 1, bgcolor: "#f1f5f9",
                                         fontSize: 12, cursor: "pointer",
                                         "&:hover": { bgcolor: "#fee2e2" },
                                     }}>
                                    {p.name} ✕
                                </Box>
                            ))}
                        </Stack>
                    )}
                </Box>
            );
        }

        case "VENDOR":
            return wrap(
                <VendorAutocomplete
                    value={value?.name ?? value ?? ""}
                    onChange={(v) => onChange(typeof v === "string" ? { name: v } : { id: v?.id, name: v?.name })}
                    placeholder="거래처명 입력 또는 선택"
                />
            );

        // 프로젝트 채널 — 예전엔 대금지급·구매품의에만 있던 선택기. 이제 양식 정의로 붙인다.
        //
        // 목록에서 고른 것만 값이 된다. 이름을 직접 치면 channel_id 가 안 붙고, 그 결재는
        // 프로젝트별 지출 취합(채널 id 기준)에서 통째로 빠진다 — 서버도 같은 이유로 거부한다.
        case "CHANNEL": {
            const channels = context?.projectChannels || [];
            // 목록이 비면 ComboBox 가 «문자열 옵션» 모드로 떨어져 타이핑한 글자가 그대로 값이 된다.
            // 그 값은 서버가 거부하므로 사용자는 이유도 모른 채 상신만 막힌다. 그래서 입력칸을
            // 아예 내리고 무엇을 해야 하는지 알려준다.
            if (channels.length === 0) {
                return wrap(<Fixed text="선택할 행사·프로젝트가 없습니다. 채널에서 프로젝트를 먼저 만들어 주세요." />);
            }
            return wrap(
                <ComboBox
                    clearable
                    placeholder="행사·프로젝트 선택"
                    value={value?.id ?? ""}
                    onChange={(v) => {
                        const ch = channels.find((c) => String(c.id) === String(v));
                        onChange(ch ? { id: ch.id, name: ch.name } : null);
                    }}
                    options={channels.map((c) => ({ value: c.id, label: c.name }))}
                />
            );
        }

        case "DEPARTMENT":
            return wrap(<TextField {...common} disabled value={context?.myInfo?.deptName ?? ""} />);

        // 다른 결재 문서 참조 — 대체휴무 사용의 '적립 근거', 결의서의 '원 품의서'.
        case "APPROVAL_REF":
            return wrap(
                <ComboBox
                    clearable
                    placeholder="대상 결재 선택"
                    value={value?.id ?? ""}
                    onChange={(v) => {
                        const a = (context?.refApprovals || []).find((x) => String(x.id) === String(v));
                        onChange(a ? { id: a.id, title: a.title } : null);
                    }}
                    options={(context?.refApprovals || []).map((a) => ({
                        value: a.id,
                        label: `${a.title} (${(a.createdAt || "").slice(0, 10)})`,
                    }))}
                />
            );

        case "GRID":
            return (
                <GridField field={field} value={value} onChange={onChange} context={context} />
            );

        // ── 기본 ─────────────────────────────────────────────────────────────
        // 모르는 타입은 텍스트로 흘린다. 관리자가 새 타입을 적어도 화면이 깨지지 않는다.
        default:
            return wrap(<TextField {...common} />);
    }
}

/**
 * 선택지 값 → 표시 라벨.
 *
 * 휴가 종류·보상기준은 값이 <b>코드</b>(ANNUAL, HALF_AM, COMP_DAY…)다. 코드를 그대로 보여주면
 * 사용자가 못 읽으므로 화면에서만 한글로 바꾼다. 저장되는 값은 여전히 코드라
 * 관리자가 라벨을 바꿔도 연차 차감이 어긋나지 않는다.
 */
function displayLabel(field, code) {
    // 슬롯이 아니라 «값» 으로 찾는다. 슬롯 기준이면 슬롯을 떼는 순간 화면에 ANNUAL·HALF_AM 같은
    // 영문 코드가 그대로 노출된다 — 실제로 «휴가 복구 신청서» 에서 그렇게 됐다.
    // 코드는 사람이 읽으라고 만든 값이 아니므로, 아는 코드면 언제나 한글로 바꿔 보여준다.
    return LEAVE_KIND_LABEL[code] ?? COMPENSATION_LABEL[code] ?? code;
}

/** 자동으로 채워지는 값 — 입력칸처럼 보이되 고칠 수 없다. */
function Fixed({ text }) {
    return (
        <Box sx={{
            px: 1.5, py: 1, borderRadius: 1, border: "1px solid #e2e8f0",
            bgcolor: "#f8fafc", fontSize: 13.5, color: "#334155",
        }}>
            {text}
        </Box>
    );
}
